import { useCallback, useMemo, useState } from 'react';
import { DiningAvailabilityError } from '@msc-b2c/interfaces/enums';
import {
  SelectedRestaurantReservation,
  UseDiningReservationSelectionParams,
} from '@msc-b2c/interfaces/interfaces';
import { fingerprintDiningReservationList } from '../utils/diningReservationFingerprint';
import { getDiningIssueCode } from '../utils/diningIssueCode';

/**
 * Manages date/time/restaurant selection for the dining flow.
 * This hook keeps form-like state and validation rules together,
 * while delegating API concerns to useDiningAvailability.
 */

export const useDiningReservationSelection = ({
  initialSelectedDate,
  initialSelectedDiningRoomCode,
  initialSelectedTime,
  initialSelectedRestaurants,
  availableDiningRooms,
  maximumSlotsBookable,
  itineraryDays,
  callDiningRoomReservations,
  onSetAvailabilityError,
  onReservationSaved,
  onReservationSaveFailed,
  onEditDateTimeRequested,
  onSingleSlotRemoved,
  cartSeedReservationsForApplyGate,
  shouldValidateSlotAvailabilityOnConfirm = true,
  onBeforeReservationCommit,
}: UseDiningReservationSelectionParams) => {
  // Seed values allow restoring context when re-entering the flow.
  const [selectedDate, setSelectedDate] = useState<string | undefined>(initialSelectedDate);
  const [selectedRestaurantCode, setSelectedRestaurantCode] = useState<string | undefined>(
    initialSelectedDiningRoomCode
  );
  const [selectedTime, setSelectedTime] = useState<string | undefined>(initialSelectedTime);
  const [selectedRestaurants, setSelectedRestaurants] = useState<SelectedRestaurantReservation[]>([
    ...(initialSelectedRestaurants ?? []),
  ]);
  const [editingReservationIndex, setEditingReservationIndex] = useState<number | null>(null);
  const [reservationEditBaseline, setReservationEditBaseline] = useState<{
    date: string;
    time: string;
    restaurantCode: string;
  } | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isCommittingSelection, setIsCommittingSelection] = useState(false);
  // Tracks whether reservations were cleared due to a pax-edit. When true, the
  // seed-gate comparison in isFooterDrivenDateTimeApplyReady is bypassed so the
  // user can re-select the originally pre-booked slot.
  const [hasDonePaxEditReset, setHasDonePaxEditReset] = useState(false);

  const selectedRestaurantsCountByCode = useMemo(() => {
    // While editing, exclude the item being edited from per-restaurant counters.
    return selectedRestaurants.reduce<Record<string, number>>((acc, reservation, idx) => {
      if (editingReservationIndex !== null && idx === editingReservationIndex) {
        return acc;
      }
      acc[reservation.restaurantCode] = (acc[reservation.restaurantCode] ?? 0) + 1;
      return acc;
    }, {});
  }, [selectedRestaurants, editingReservationIndex]);

  const handleSelectDate = useCallback(
    (date: string, _dayNumber: number) => {
      // Date change invalidates previously chosen restaurant/time.
      setSelectedDate(date || undefined);
      setSelectedTime(undefined);
      setSelectedRestaurantCode(undefined);
      onSetAvailabilityError(undefined);
    },
    [onSetAvailabilityError]
  );

  const handleSelectTime = useCallback(
    (time: string) => {
      setSelectedTime(time);
      onSetAvailabilityError(undefined);
    },
    [onSetAvailabilityError]
  );

  const handleSelectRestaurantTime = useCallback(
    (restaurantCode: string, time: string) => {
      const selectedRestaurant = availableDiningRooms.find((room) => room.code === restaurantCode);
      const alreadySelectedForRestaurant = selectedRestaurantsCountByCode[restaurantCode] ?? 0;
      const perRestaurantLimit = Math.max(
        1,
        selectedRestaurant?.maximumBookingLimit ??
          selectedRestaurant?.allowedQuantity ??
          maximumSlotsBookable
      );

      // Pre-check: block selection immediately when the restaurant cap is already reached.
      if (alreadySelectedForRestaurant >= perRestaurantLimit) {
        setSelectedRestaurantCode(restaurantCode);
        setSelectedTime(undefined);
        onSetAvailabilityError(DiningAvailabilityError.RESTAURANT_LIMIT);
        return;
      }

      onSetAvailabilityError(undefined);
      setSelectedRestaurantCode(restaurantCode);
      setSelectedTime(time);
    },
    [
      availableDiningRooms,
      maximumSlotsBookable,
      onSetAvailabilityError,
      selectedRestaurantsCountByCode,
    ]
  );

  const handleConfirmDateTimeSelection = useCallback(async () => {
    if (!selectedDate || !selectedTime || !selectedRestaurantCode) {
      return;
    }

    const selectedRestaurant = availableDiningRooms.find(
      (room) => room.code === selectedRestaurantCode
    );

    const selectedRestaurantCurrentCount =
      selectedRestaurantsCountByCode[selectedRestaurantCode] ?? 0;
    const selectedRestaurantLimit = Math.max(
      1,
      selectedRestaurant?.maximumBookingLimit ??
        selectedRestaurant?.allowedQuantity ??
        maximumSlotsBookable
    );

    if (
      editingReservationIndex === null &&
      selectedRestaurantCurrentCount >= selectedRestaurantLimit
    ) {
      // Restaurant-level cap must be evaluated before package-level cap.
      onSetAvailabilityError(DiningAvailabilityError.RESTAURANT_LIMIT);
      return;
    }

    if (editingReservationIndex === null && selectedRestaurants.length >= maximumSlotsBookable) {
      onSetAvailabilityError(DiningAvailabilityError.PER_PACKAGE_LIMIT);
      return;
    }

    setIsConfirming(true);

    try {
      if (shouldValidateSlotAvailabilityOnConfirm) {
        await callDiningRoomReservations({
          diningRoomCode: selectedRestaurantCode,
          date: selectedDate,
          time: selectedTime,
        });
      }

      const selectedDay = itineraryDays.find((day) => day.date === selectedDate);

      const nextReservation: SelectedRestaurantReservation = {
        restaurantCode: selectedRestaurantCode,
        restaurantName: selectedRestaurant?.description,
        restaurantCategory: '',
        date: selectedDate,
        time: selectedTime,
        dayNumber: selectedDay?.dayNumber,
        portName: selectedDay?.portName,
        isAtSea: selectedDay?.isAtSea,
      };

      const nextSelectedRestaurants =
        editingReservationIndex === null
          ? [...selectedRestaurants, nextReservation]
          : selectedRestaurants.map((reservation, idx) =>
              idx === editingReservationIndex ? nextReservation : reservation
            );

      onBeforeReservationCommit?.();
      setIsCommittingSelection(true);
      await onReservationSaved({
        nextSelectedRestaurants,
        wasEditingReservation: editingReservationIndex !== null,
        savedReservation: nextReservation,
      });

      // Commit selection only after async save flow resolves to avoid optimistic UI drift.
      setSelectedRestaurants(nextSelectedRestaurants);
      setEditingReservationIndex(null);
      setReservationEditBaseline(null);
    } catch (error) {
      onReservationSaveFailed?.(getDiningIssueCode(error));
    } finally {
      setIsCommittingSelection(false);
      setIsConfirming(false);
    }
  }, [
    availableDiningRooms,
    callDiningRoomReservations,
    editingReservationIndex,
    itineraryDays,
    maximumSlotsBookable,
    onReservationSaved,
    onReservationSaveFailed,
    onSetAvailabilityError,
    onBeforeReservationCommit,
    selectedDate,
    selectedRestaurantsCountByCode,
    selectedRestaurantCode,
    selectedRestaurants,
    shouldValidateSlotAvailabilityOnConfirm,
    selectedTime,
  ]);

  const handleEditReservation = useCallback(
    (reservationIndex: number) => {
      const reservation = selectedRestaurants[reservationIndex];
      if (!reservation) {
        return;
      }

      setEditingReservationIndex(reservationIndex);
      setSelectedDate(reservation.date);
      setSelectedTime(reservation.time);
      setSelectedRestaurantCode(reservation.restaurantCode);
      setReservationEditBaseline({
        date: reservation.date,
        time: reservation.time,
        restaurantCode: reservation.restaurantCode,
      });
      onSetAvailabilityError(undefined);
      onEditDateTimeRequested();
    },
    [onEditDateTimeRequested, onSetAvailabilityError, selectedRestaurants]
  );

  const handleRemoveReservation = useCallback(
    (reservationIndex: number) => {
      const isRemovingLastReservation =
        reservationIndex >= 0 &&
        reservationIndex < selectedRestaurants.length &&
        selectedRestaurants.length === 1;

      // Keep next DATE_TIME selection clean after deletion.
      setSelectedRestaurants((prev) => prev.filter((_, idx) => idx !== reservationIndex));
      setEditingReservationIndex(null);
      setReservationEditBaseline(null);
      setSelectedDate(undefined);
      setSelectedTime(undefined);
      setSelectedRestaurantCode(undefined);
      onSetAvailabilityError(undefined);

      if (isRemovingLastReservation) {
        // If recap becomes empty, return to choose-when-book to let the user pick reservation mode.
        onSingleSlotRemoved();
      }
    },
    [onSetAvailabilityError, onSingleSlotRemoved, selectedRestaurants.length]
  );

  const handleAddAnotherReservation = useCallback(() => {
    // Validate that we haven't reached the maximum capacity before allowing another reservation
    if (selectedRestaurants.length >= maximumSlotsBookable) {
      onSetAvailabilityError(DiningAvailabilityError.PER_PACKAGE_LIMIT);
      return;
    }

    setEditingReservationIndex(null);
    setReservationEditBaseline(null);
    setSelectedDate(undefined);
    setSelectedTime(undefined);
    setSelectedRestaurantCode(undefined);
    onSetAvailabilityError(undefined);
    onEditDateTimeRequested();
  }, [
    onEditDateTimeRequested,
    onSetAvailabilityError,
    selectedRestaurants.length,
    maximumSlotsBookable,
  ]);

  const resetReservationsForPassengerEdit = useCallback(() => {
    setSelectedRestaurants([]);
    setEditingReservationIndex(null);
    setReservationEditBaseline(null);
    setSelectedDate(undefined);
    setSelectedTime(undefined);
    setSelectedRestaurantCode(undefined);
    onSetAvailabilityError(undefined);
    setHasDonePaxEditReset(true);
  }, [onSetAvailabilityError]);

  const isFooterDrivenDateTimeApplyReady = useMemo(() => {
    if (!selectedDate || !selectedTime || !selectedRestaurantCode) {
      return false;
    }

    const pendingFingerprint = fingerprintDiningReservationList([
      {
        restaurantCode: selectedRestaurantCode,
        date: selectedDate,
        time: selectedTime,
      },
    ]);

    if (editingReservationIndex !== null && reservationEditBaseline) {
      const baselineFingerprint = fingerprintDiningReservationList([
        {
          restaurantCode: reservationEditBaseline.restaurantCode,
          date: reservationEditBaseline.date,
          time: reservationEditBaseline.time,
        },
      ]);
      return pendingFingerprint !== baselineFingerprint;
    }

    const seed = cartSeedReservationsForApplyGate ?? [];
    // After a pax-edit reset the gate must be skipped: the user legitimately
    // wants to rebook the same slot with a different passenger selection.
    if (!hasDonePaxEditReset && selectedRestaurants.length === 0 && seed.length === 1) {
      return pendingFingerprint !== fingerprintDiningReservationList(seed);
    }

    return true;
  }, [
    cartSeedReservationsForApplyGate,
    editingReservationIndex,
    hasDonePaxEditReset,
    reservationEditBaseline,
    selectedDate,
    selectedRestaurantCode,
    selectedRestaurants.length,
    selectedTime,
  ]);

  return {
    selectedDate,
    selectedRestaurantCode,
    selectedTime,
    selectedRestaurants,
    editingReservationIndex,
    isConfirming,
    isCommittingSelection,
    isFooterDrivenDateTimeApplyReady,
    selectedRestaurantsCountByCode,
    handleSelectDate,
    handleSelectTime,
    handleSelectRestaurantTime,
    handleConfirmDateTimeSelection,
    handleEditReservation,
    handleRemoveReservation,
    handleAddAnotherReservation,
    resetReservationsForPassengerEdit,
  };
};
